import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Move } from "lucide-react";

/**
 * Cinematic before/after image comparison slider.
 * Drag or click to reveal.
 */
export default function BeforeAfter({ before, after, label = "Storefront · SoHo", testid }) {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  function updateFromClientX(clientX) {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = Math.max(4, Math.min(96, ((clientX - rect.left) / rect.width) * 100));
    setPos(p);
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[16/10] overflow-hidden select-none"
      style={{ borderRadius: "var(--r-2xl)", border: "1px solid var(--line-gold)", boxShadow: "0 44px 84px -20px rgba(0,0,0,0.85), 0 0 40px rgba(198,168,124,0.1)" }}
      onMouseDown={(e) => { dragging.current = true; updateFromClientX(e.clientX); }}
      onMouseMove={(e) => { if (dragging.current) updateFromClientX(e.clientX); }}
      onMouseUp={() => (dragging.current = false)}
      onMouseLeave={() => (dragging.current = false)}
      onTouchStart={(e) => { dragging.current = true; updateFromClientX(e.touches[0].clientX); }}
      onTouchMove={(e) => { if (dragging.current) updateFromClientX(e.touches[0].clientX); }}
      onTouchEnd={() => (dragging.current = false)}
      data-testid={testid}
      data-cursor="hover"
      data-cursor-label="Drag"
    >
      {/* AFTER — full base image */}
      <img src={after} alt="After" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.98) contrast(1.05) saturate(0.95)" }} />

      {/* BEFORE — clipped */}
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt="Before" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.85) contrast(0.92) saturate(0.75) sepia(0.15)" }} />
      </div>

      {/* Divider line + handle */}
      <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-px h-full" style={{ background: "linear-gradient(180deg, transparent, var(--gold) 15%, var(--gold) 85%, transparent)", boxShadow: "0 0 20px var(--gold)" }} />
      </div>
      <motion.div
        className="absolute pointer-events-none"
        style={{ left: `${pos}%`, top: "50%", transform: "translate(-50%,-50%)" }}
        animate={{ scale: dragging.current ? 1.15 : 1 }}
      >
        <div
          className="w-14 h-14 flex items-center justify-center backdrop-blur-md"
          style={{
            borderRadius: "var(--r-full)",
            background: "rgba(11,11,13,0.7)",
            border: "1px solid var(--gold)",
            boxShadow: "0 12px 32px -6px rgba(0,0,0,0.8), 0 0 28px rgba(198,168,124,0.4)",
          }}
        >
          <Move size={18} style={{ color: "var(--gold)" }} strokeWidth={1.5} />
        </div>
      </motion.div>

      {/* Labels */}
      <div className="absolute top-5 left-5 pill" style={{ background: "rgba(11,11,13,0.72)" }}>Before</div>
      <div className="absolute top-5 right-5 pill" style={{ background: "rgba(198,168,124,0.14)", color: "var(--gold-2)" }}>After · Bespoke</div>
      <div className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.36em] px-3 py-2" style={{ background: "rgba(11,11,13,0.7)", color: "var(--text-2)", border: "1px solid var(--line)", borderRadius: "var(--r-full)", backdropFilter: "blur(8px)" }}>{label}</div>
    </div>
  );
}
